import { Link } from "react-router-dom";
import { ArrowRight, Check, Globe2, Landmark, FileSpreadsheet, Plane } from "lucide-react";
import SectionHeader from "../components/SectionHeader";
import { ConsultationDialog } from "../components/ConsultationDialog";

export default function NRIServicesPage() {
  return (
    <main data-testid="nri-page" className="pt-32">
      {/* Hero */}
      <section className="relative pb-20">
        <div className="container-myc grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow">NRI Services</p>
            <h1 className="h-display-1 text-[#2A0A0E] mt-6">
              India,{" "}
              <span className="italic font-normal text-[#7A1F26]">
                advised properly
              </span>{" "}
              — from anywhere in the world.
            </h1>
            <p className="text-slate-600/70 mt-7 leading-relaxed text-lg max-w-xl">
              For NRIs across the GCC, North America, the UK and Singapore — a
              compliant, tax-efficient investing partner that handles the
              regulation, paperwork and reporting end-to-end.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <ConsultationDialog
                defaultService="NRI Investment Services"
                trigger={
                  <button data-testid="nri-hero-consult-button" className="btn-gold">
                    Book NRI Consultation <ArrowRight className="w-4 h-4" />
                  </button>
                }
              />
              <Link to="/contact" className="btn-outline-gold" data-testid="nri-hero-contact-link">
                Contact NRI Desk
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1569427355692-3943380335c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHx3ZWFsdGglMjBtYW5hZ2VtZW50JTIwYmFua2luZyUyMGx1eHVyeXxlbnwwfHx8fDE3Nzc1NDU3MTd8MA&ixlib=rb-4.1.0&q=85"
              alt="NRI investing in India"
              className="w-full h-[480px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* What we cover */}
      <section className="py-24 bg-[#FAF5EB]">
        <div className="container-myc">
          <SectionHeader
            eyebrow="Coverage"
            title="A complete framework for cross-border investing."
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-900/5 mt-14 border border-slate-200/5">
            {[
              { i: Landmark, t: "Account Architecture", d: "NRE, NRO, FCNR(B), and PIS-enabled accounts with the right banks." },
              { i: Globe2, t: "Investment Access", d: "Mutual funds, equity, AIFs, PMS, FDs, bonds and GIFT City funds." },
              { i: FileSpreadsheet, t: "Tax & Compliance", d: "DTAA, Form 10F, TRC, FATCA/CRS, and annual filing support." },
              { i: Plane, t: "Repatriation & Estate", d: "Repatriation playbooks, India Wills and cross-border succession." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="bg-[#FAF5EB] p-8">
                <Icon className="w-6 h-6 text-[#7A1F26]" />
                <h3 className="font-display text-lg text-[#2A0A0E] mt-6">{t}</h3>
                <p className="text-sm text-slate-600/55 mt-3 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24">
        <div className="container-myc grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionHeader
              eyebrow="Onboarding"
              title="A four-step path from first call to first allocation."
            />
          </div>
          <div className="lg:col-span-7 space-y-3">
            {[
              { n: "01", t: "Discovery call", d: "Goals, residency, taxation, and existing portfolio review." },
              { n: "02", t: "Account setup", d: "We coordinate NRE/NRO/PIS account opening with banking partners." },
              { n: "03", t: "Allocation plan", d: "A written IPS and tax-aware allocation across India and GIFT City." },
              { n: "04", t: "Ongoing reviews", d: "Quarterly reviews, performance attribution, and annual tax support." },
            ].map((s) => (
              <div
                key={s.n}
                data-testid={`nri-step-${s.n}`}
                className="grid grid-cols-12 items-center bg-[#FAF5EB] hover:bg-[#F1F5F9] border-l-2 border-[#7A1F26]/40 hover:border-[#7A1F26] transition-all p-6"
              >
                <p className="font-display text-3xl text-[#7A1F26] col-span-2">{s.n}</p>
                <div className="col-span-10">
                  <h4 className="text-[#2A0A0E] font-medium">{s.t}</h4>
                  <p className="text-sm text-slate-600/60 mt-1.5">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#FAF5EB]">
        <div className="container-myc">
          <SectionHeader eyebrow="FAQs" title="Common questions from NRI investors." />
          <div className="mt-14 space-y-4 max-w-4xl">
            {[
              {
                q: "I'm a US-resident NRI — can I invest in Indian mutual funds?",
                a: "Yes, but a limited set of AMCs accept US-resident NRIs due to FATCA. We map you only to compliant AMCs and structures, and prefer GIFT City IFSC funds where appropriate.",
              },
              {
                q: "Do I need a PIS account to invest in stocks?",
                a: "PIS is required for NRE-route stock investing. NRO-route does not require PIS but has different tax & repatriation rules. We help you choose and set up correctly.",
              },
              {
                q: "Can my investments be repatriated overseas?",
                a: "Yes — funds invested via NRE / FCNR are fully repatriable; NRO investments are repatriable up to USD 1M per year subject to compliance. We orchestrate the documentation.",
              },
              {
                q: "How is taxation managed across two countries?",
                a: "We work with your tax advisor (or our partners) to apply the relevant DTAA, secure your TRC and Form 10F, and minimise double taxation through proper structuring.",
              },
            ].map((f) => (
              <details
                key={f.q}
                data-testid={`nri-faq-${f.q.slice(0, 12).replace(/\s/g, "-")}`}
                className="group bg-[#FAF5EB] border border-slate-200/5 hover:border-[#7A1F26]/30 transition-colors p-6"
              >
                <summary className="cursor-pointer flex items-center justify-between text-[#2A0A0E] font-medium list-none">
                  {f.q}
                  <span className="text-[#7A1F26] text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-slate-600/65 mt-4 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-myc text-center max-w-2xl mx-auto">
          <h2 className="h-display-2 text-[#2A0A0E]">
            Wherever you live, we will handle the India side.
          </h2>
          <p className="text-slate-600/65 mt-5">
            A confidential conversation with our NRI desk — across timezones.
          </p>
          <div className="mt-10">
            <ConsultationDialog
              defaultService="NRI Investment Services"
              trigger={
                <button data-testid="nri-cta-consult-button" className="btn-gold">
                  Speak with NRI Desk <ArrowRight className="w-4 h-4" />
                </button>
              }
            />
          </div>
        </div>
      </section>
    </main>
  );
}
